import { Form, FormRow, PopupHint, Select } from '@op/opds.opux';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE } from '../../constants/actionTypes';
import { DEFAULT_LOCALE } from '../../constants/common';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import { FormInput } from '../common/Input/FormInput';

const ValuablesQuestions = (insurance) => {
  const { state, validateInsurance } = useInsuranceOffer();

  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });

  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };
  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );

  const valubleDetails = state.selectedInsurances[index].questions;

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };
  

  return (
    <Form spacing="default">
      <div>
        <FormRow
          dataKey={`${insurance.id}-valuableType`}
          label={<Message id="labelText.valuableType" />}
          labelFor={buildElementId(insurance.id, 'VALUABLE_TYPE')}
          required
        >
          <Select
            value={getValue(valubleDetails.valuableType)}
            aria-describedby='valuableTypeError'
            name={`${insurance.id}-valuableType`}
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  valuableType: makeInputData(
                    e.target.value,
                    'valuableType',
                    getOptionTitle(e, 'valuableType'),
                    'error.fill.in.missing.information'
                  ),
                },
                questionId: e.target.id,
              });
            }}
            id={buildElementId(insurance.id, 'VALUABLE_TYPE')}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="Musical" key="Musical">
              {getMessage('option.valuableType.001')}
            </option>
            <option value="Camera and accessories" key="Camera and accessories">
              {getMessage('option.valuableType.002')}
            </option>
            <option value="Lens" key="Lens">
              {getMessage('option.valuableType.003')}
            </option>
            <option value="Weapon" key="Weapon">
              {getMessage('option.valuableType.004')}
            </option>
            <option value="Watch" key="Watch">
              {getMessage('option.valuableType.005')}
            </option>
            <option value="Jewellery" key="Jewellery">
              {getMessage('option.valuableType.006')}
            </option>
          </Select>
          <ValidationError
            id="valuableTypeError"
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'VALUABLE_TYPE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>
      {/* Make and model questions */}
      {getValue(valubleDetails.valuableType) !== '' && getValue(valubleDetails.valuableType) !== 'choose' && getValue(valubleDetails.valuableType) !== 'Weapon' && getValue(valubleDetails.valuableType) !== 'Jewellery' && (
        <div>
          <FormInput
              type="stringwithoutSP"
              maxLength={100}
              value={getValue(valubleDetails.valuableMake)}
              inputId={buildElementId(insurance.id,'VALUABLE_MAKE')}
              onChange={(valuableMake , error) => {
                valubleDetails.valuableMake = makeInputData(
                  valuableMake,
                  'make'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: valubleDetails,
                  questionId: `${insurance.id}-valuableMake`,
                  error,
                });                
              }}
              label={getMessage('labelText.make')}
              required
            />

          <FormInput
            type="stringwithoutSP"
            maxLength={100}
            value={getValue(valubleDetails.valuableModel)}
            inputId={buildElementId(insurance.id,'VALUABLE_MODEL')}
            onChange={(valuableModel , error) => {
              valubleDetails.valuableModel = makeInputData(
                valuableModel,
                'model'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-valuableModel`,
                error,
              });                
            }}
            label={getMessage('labelText.model')}
            required
          /> 
        </div>
      )}
      {/* Device Number question */}
      {getValue(valubleDetails.valuableType) !== '' && getValue(valubleDetails.valuableType) !== 'choose' && (getValue(valubleDetails.valuableType) === 'Camera and accessories' || getValue(valubleDetails.valuableType) === 'Lens') && (
        <div>
          <FormInput
              type="stringwithoutSP"
              maxLength={100}
              value={getValue(valubleDetails.valuableDeviceNumber)}
              inputId={buildElementId(insurance.id,'VALUABLE_DEVICE_NUMBER')}
              onChange={(valuableDeviceNumber , error) => {
                valubleDetails.valuableDeviceNumber = makeInputData(
                  valuableDeviceNumber,
                  'deviceNumber'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: valubleDetails,
                  questionId: `${insurance.id}-valuableDeviceNumber`,
                  error,
                });                
              }}
              label={getMessage('labelText.deviceNumber')}
              required
            />
        </div>
      )}
      {/* Musical instrument Questions */}
      {getValue(valubleDetails.valuableType) === 'Musical' && (
        <div>
          <FormInput
            type="year"
            maxLength={4}
            limit={1900}
            value={getValue(valubleDetails.FirstYearOfServiceInstrument)}
            inputId={buildElementId(insurance.id, 'VALUABLE_FIRSTYEAROFSERVICEINSTRUMENT')}
            onChange={(FirstYearOfServiceInstrument , error) => {
              valubleDetails.FirstYearOfServiceInstrument = makeInputData(
                FirstYearOfServiceInstrument,
                'FirstYearOfService'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-FirstYearOfService`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstYearOfService')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.FirstYearOfService" />
              </PopupHint>
            }
            required
          />
          <FormInput
            type="numeric"
            limit={999999}
            value={getValue(valubleDetails.valueInstrument)}
            inputId={buildElementId(insurance.id, 'VALUABLE_VALUEINSTRUMENT')}
            onChange={(valueInstrument , error) => {
              valubleDetails.valueInstrument = makeInputData(
                valueInstrument,
                'valueInstrument'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-valueInstrument`,
                error,
              });                
            }}
            label={getMessage('labelText.valueInstrument')}
            amount
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            hint={
              <PopupHint placement="top">
                <Message id="hintText.value" />
              </PopupHint>
            }
            required
          />         
        </div>
      )}

      {/* Camera and accessories Questions */}
      {getValue(valubleDetails.valuableType) === 'Camera and accessories' && (
        <div>
          <FormInput
            type="year"
            maxLength={4}
            limit={1900}
            value={getValue(valubleDetails.FirstYearOfServiceCamera)}
            inputId={buildElementId(insurance.id, 'VALUABLE_FIRSTYEAROFSERVICECAMERA')}
            onChange={(FirstYearOfServiceCamera , error) => {
              valubleDetails.FirstYearOfServiceCamera = makeInputData(
                FirstYearOfServiceCamera,
                'FirstYearOfService'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-FirstYearOfService`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstYearOfService')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.FirstYearOfService" />
              </PopupHint>
            }
            required
          />

          <FormInput
            type="numeric"
            limit={999999}
            value={getValue(valubleDetails.valueCamera)}
            inputId={buildElementId(insurance.id, 'VALUABLE_VALUECAMERA')}
            onChange={(valueCamera , error) => {
              valubleDetails.valueCamera = makeInputData(
                valueCamera,
                'valueCamera'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-valueCamera`,
                error,
              });                
            }}
            label={getMessage('labelText.valueCamera')}
            amount
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            hint={
              <PopupHint placement="top">
                <Message id="hintText.value" />
              </PopupHint>
            }
            required
          />
                   
        </div>
      )}

      {/* Lens Questions */}
      {getValue(valubleDetails.valuableType) === 'Lens' && (
        <div>
          <FormInput
            type="year"
            maxLength={4}
            limit={1900}
            value={getValue(valubleDetails.FirstYearOfServiceLens)}
            inputId={buildElementId(insurance.id, 'VALUABLE_FIRSTYEAROFSERVICELENS')}
            onChange={(FirstYearOfServiceLens , error) => {
              valubleDetails.FirstYearOfServiceLens = makeInputData(
                FirstYearOfServiceLens,
                'FirstYearOfService'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-FirstYearOfService`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstYearOfService')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.FirstYearOfService" />
              </PopupHint>
            }
            required
          />

          <FormInput
            type="numeric"
            limit={999999}
            value={getValue(valubleDetails.valueLens)}
            inputId={buildElementId(insurance.id, 'VALUABLE_VALUELENS')}
            onChange={(valueLens , error) => {
              valubleDetails.valueLens = makeInputData(
                valueLens,
                'valueLens'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-valueLens`,
                error,
              });                
            }}
            label={getMessage('labelText.valueLens')}
            amount
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            hint={
              <PopupHint placement="top">
                <Message id="hintText.value" />
              </PopupHint>
            }
            required
          />         
        </div>
      )}

      {/* Weapon Questions */}
      {getValue(valubleDetails.valuableType) === 'Weapon' && (
        <div>
          <FormInput
            type="year"
            maxLength={4}
            limit={1900}
            value={getValue(valubleDetails.FirstYearOfServiceWeapon)}
            inputId={buildElementId(insurance.id, 'VALUABLE_FIRSTYEAROFSERVICEWEAPON')}
            onChange={(FirstYearOfServiceWeapon , error) => {
              valubleDetails.FirstYearOfServiceWeapon = makeInputData(
                FirstYearOfServiceWeapon,
                'FirstYearOfService'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-FirstYearOfService`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstYearOfService')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.FirstYearOfService" />
              </PopupHint>
            }
            required
          />

          <FormInput
            type="numeric"
            limit={999999}
            value={getValue(valubleDetails.valueWeapon)}
            inputId={buildElementId(insurance.id, 'VALUABLE_VALUEWEAPON')}
            onChange={(valueWeapon , error) => {
              valubleDetails.valueWeapon = makeInputData(
                valueWeapon,
                'valueWeapon'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-valueWeapon`,
                error,
              });                
            }}
            label={getMessage('labelText.valueWeapon')}
            amount
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            hint={
              <PopupHint placement="top">
                <Message id="hintText.value" />
              </PopupHint>
            }
            required
          />
        </div>
      )}

      {/* Watch Questions */}
      {getValue(valubleDetails.valuableType) === 'Watch' && (
        <div>
          <FormInput
              type="stringwithoutSP"
              maxLength={100}
              value={getValue(valubleDetails.valuableSerialNumber)}
              inputId={buildElementId(insurance.id,'VALUABLE_SERIAL_NUMBER')}
              onChange={(valuableSerialNumber , error) => {
                valubleDetails.valuableSerialNumber = makeInputData(
                  valuableSerialNumber,
                  'serialNumber'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: valubleDetails,
                  questionId: `${insurance.id}-valuableSerialNumber`,
                  error,
                });                
              }}
              label={getMessage('labelText.serialNumber')}
              required={false}
          />
          <FormInput
            type="year"
            maxLength={4}
            limit={1900}
            value={getValue(valubleDetails.FirstYearOfServiceWatch)}
            inputId={buildElementId(insurance.id, 'VALUABLE_FIRSTYEAROFSERVICEWATCH')}
            onChange={(FirstYearOfServiceWatch , error) => {
              valubleDetails.FirstYearOfServiceWatch = makeInputData(
                FirstYearOfServiceWatch,
                'FirstYearOfService'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-FirstYearOfService`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstYearOfService')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.FirstYearOfService" />
              </PopupHint>
            }
            required
          />

          <FormInput
            type="numeric"
            limit={999999}
            value={getValue(valubleDetails.valueWatch)}
            inputId={buildElementId(insurance.id, 'VALUABLE_VALUEWATCH')}
            onChange={(valueWatch , error) => {
              valubleDetails.valueWatch = makeInputData(
                valueWatch,
                'valueWatch'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-valueWatch`,
                error,
              });                
            }}
            label={getMessage('labelText.valueWatch')}
            amount
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            hint={
              <PopupHint placement="top">
                <Message id="hintText.value" />
              </PopupHint>
            }
            required
          />         
        </div>
      )}

      {/* Jewellery Questions */}
      {getValue(valubleDetails.valuableType) === 'Jewellery' && (
        <div>
          <FormInput
              type="stringwithoutSP"
              maxLength={100}
              value={getValue(valubleDetails.valuableWhichJewellery)}
              inputId={buildElementId(insurance.id,'VALUABLE_WHICH_JEWELLERY')}
              onChange={(valuableWhichJewellery , error) => {
                valubleDetails.valuableWhichJewellery = makeInputData(
                  valuableWhichJewellery,
                  'whichJewellery'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: valubleDetails,
                  questionId: `${insurance.id}-valuableWhichJewellery`,
                  error,
                });                
              }}
              label={getMessage('labelText.whichJewellery')}
              required
          />
          <FormInput
            type="year"
            maxLength={4}
            limit={1900}
            value={getValue(valubleDetails.FirstYearOfServiceJewellery)}
            inputId={buildElementId(insurance.id, 'VALUABLE_FIRSTYEAROFSERVICEJEWELLERY')}
            onChange={(FirstYearOfServiceJewellery , error) => {
              valubleDetails.FirstYearOfServiceJewellery = makeInputData(
                FirstYearOfServiceJewellery,
                'FirstYearOfService'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-FirstYearOfService`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstYearOfService')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.FirstYearOfService" />
              </PopupHint>
            }
            required
          />

          <FormInput
            type="numeric"
            limit={999999}
            value={getValue(valubleDetails.valueJewellery)}
            inputId={buildElementId(insurance.id, 'VALUABLE_VALUEJEWELLERY')}
            onChange={(valueJewellery , error) => {
              valubleDetails.valueJewellery = makeInputData(
                valueJewellery,
                'valueJewellery'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: valubleDetails,
                questionId: `${insurance.id}-valueJewellery`,
                error,
              });                
            }}
            label={getMessage('labelText.valueJewellery')}
            amount
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            hint={
              <PopupHint placement="top">
                <Message id="hintText.value" />
              </PopupHint>
            }
            required
          />         
        </div>
      )}
    </Form>
  );
};

export default ValuablesQuestions;
